import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';

/**
 * Event handles targeted by the honeymoon stays-page redesign experiment.
 *
 * Primary scope for the redesign — add more handles here if the experiment
 * expands. Paired with the `staysPageRedesignEnabled` feature flag below as
 * an AND gate: both the event-handle check AND the flag must resolve true
 * for the redesign to render.
 */
export const STAYS_REDESIGN_EVENT_HANDLES: ReadonlySet<string> = new Set(['joy-travel', 'joy-and-travel']);

/**
 * True when the redesign should render for the currently loaded guest event.
 *
 * Gate is `eventHandle ∈ STAYS_REDESIGN_EVENT_HANDLES  &&  staysPageRedesignEnabled`.
 * - Event-handle scope prevents any URL/flag override from leaking the
 *   redesign onto non-target events (this was the PR #14394 revert root cause).
 * - `staysPageRedesignEnabled` defaults to `'off'` for safety, so the
 *   redesign stays dark until growth flips it to `'on'` via remote config
 *   for the target events. Once enabled, flipping back to `'off'` acts as a
 *   kill-switch without a code revert. Locally you can preview with
 *   `?feature.staysPageRedesignEnabled=on`.
 *
 * Safe during SSR — returns false until the event handle resolves.
 */
export const useIsStaysRedesignActive = (): boolean => {
  const { eventHandle } = useEventInfo();
  const [isFlagOn] = useIsFeatureEnabled('staysPageRedesignEnabled');
  return !!eventHandle && STAYS_REDESIGN_EVENT_HANDLES.has(eventHandle) && isFlagOn;
};
